import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

/**
 * Recharge le Feed quand l'utilisateur y revient — sans qu'il ait rien à faire.
 *
 * Deux moments, les seuls qui comptent en pratique :
 *  - l'app native repasse au premier plan (`appStateChange`) ;
 *  - l'onglet du navigateur redevient visible (`visibilitychange`).
 *
 * Volontairement PAS de sondage périodique : un Feed ouvert et laissé de côté
 * n'a pas besoin d'interroger le serveur toutes les minutes. L'arrivée d'une
 * notification push recharge déjà le fil en direct (cf. `main.tsx`).
 *
 * Monté par la page Feed uniquement : rien ne tourne ailleurs dans l'app.
 */
export function useFeedAutoRefresh(refresh: () => void | Promise<void>): void {
  useEffect(() => {
    let disposed = false;
    const run = () => {
      if (!disposed) void refresh();
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') run();
    };
    document.addEventListener('visibilitychange', onVisible);

    let removeNative: (() => void) | null = null;
    if (Capacitor.isNativePlatform()) {
      void import('@capacitor/app')
        .then(({ App }) =>
          App.addListener('appStateChange', ({ isActive }) => {
            if (isActive) run();
          }),
        )
        .then((sub) => {
          if (disposed) void sub.remove();
          else removeNative = () => void sub.remove();
        })
        .catch(() => {
          /* pas de retour au premier plan : le bouton reste */
        });
    }

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisible);
      removeNative?.();
    };
  }, [refresh]);
}
